import { useCallback, useEffect, useState } from "react";
import { Search } from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { ApiError } from "@shared-core/data/api/errors";
import { activationHint } from "@shared-core/domain/campaign-calendar";
import {
  extractActivationCapExceeded,
  type ActivationCapWarning,
} from "@shared-core/data/validation";
import type {
  CadenceStep,
  MailchimpCampaignMatch,
  MailchimpSyncState,
} from "@shared-core/data/marketing-types";
import {
  formatDateOrDash,
  formatDateTimeOrDash,
} from "@shared-core/domain/formatters";
import { useIsAdmin } from "../../../auth/hooks";
import { CampaignEmailPreviewTab } from "./CampaignEmailPreviewTab";
import { CampaignEngagementTab } from "./CampaignEngagementTab";
import { Button } from "../../primitives/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../primitives/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../primitives/alert-dialog";
import { Input } from "../../primitives/input";
import { Label } from "../../primitives/label";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../primitives/tabs";

function describeSkippedContacts(
  syncState: MailchimpSyncState | null,
): string | null {
  if (!syncState || syncState.skipped_count <= 0) return null;
  return (
    syncState.last_error ??
    `${syncState.skipped_count} ${syncState.skipped_count === 1 ? "contact" : "contacts"} could not be synced to Mailchimp.`
  );
}

/**
 * The reusable "one send's own operational detail" block: Mailchimp
 * campaign-id capture (with the campaign-name finder), the activate action
 * (including the frequency-cap confirm dialog), the Mailchimp sync warning
 * + resync fixer, and the per-send Engagement/Email-preview tabs.
 *
 * This is the campaign-projects rework of what used to be whole-campaign
 * chrome inlined in CampaignWorkspacePage — every one of those behaviors is
 * unchanged in substance, only re-scoped from "the whole campaign" to "one
 * cadence step (a send)", per the module spec. Used both for a single-send
 * campaign (rendered directly on the page) and for each row of a
 * multi-send campaign's master-detail list.
 */
export function CampaignSendDetail({
  campaignId,
  step,
  onStepChange,
}: {
  campaignId: string;
  step: CadenceStep;
  onStepChange: (updated: CadenceStep) => void;
}) {
  const dataAdapter = useDataAdapter();
  const isAdmin = useIsAdmin();
  const [activating, setActivating] = useState(false);
  const [activateError, setActivateError] = useState<string | null>(null);
  const [capWarnings, setCapWarnings] = useState<ActivationCapWarning[]>([]);
  const [capDialogOpen, setCapDialogOpen] = useState(false);

  // The real Mailchimp send date this tranche is scheduled against
  // (CadenceStep.scheduled_send_date) — never settable on a reminder step,
  // whose own effective date always derives from its original tranche's
  // real send date (see docs/features/campaign-scheduling/). CIS's own
  // activation date (when it pushes the audience) is derived from this
  // client-side too, purely for the live preview text below — the value
  // CIS actually acts on is computed server-side from the saved date.
  const [sendDateInput, setSendDateInput] = useState(
    step.scheduled_send_date ?? "",
  );
  const [savingSendDate, setSavingSendDate] = useState(false);
  const [sendDateError, setSendDateError] = useState<string | null>(null);
  const [sendDateSaved, setSendDateSaved] = useState(false);

  const [mailchimpIdInput, setMailchimpIdInput] = useState(
    step.mailchimp_campaign_id ?? "",
  );
  const [savingMailchimpId, setSavingMailchimpId] = useState(false);
  const [mailchimpIdError, setMailchimpIdError] = useState<string | null>(null);
  const [mailchimpIdSaved, setMailchimpIdSaved] = useState(false);

  const [mailchimpSkipWarning, setMailchimpSkipWarning] = useState<
    string | null
  >(null);
  const [resyncing, setResyncing] = useState(false);
  const [resyncError, setResyncError] = useState<string | null>(null);
  const [resyncedAt, setResyncedAt] = useState<string | null>(null);

  // One-off, admin-only backfill for a send whose Mailchimp campaign ID
  // predates CIS's own touch-writing paths (see backfillStepTouches) —
  // deliberately separate from "Fix pending touches" above, which only
  // resyncs this send's own frozen StepMember rows and does nothing for a
  // send that never had any (e.g. a very old legacy campaign).
  const [backfilling, setBackfilling] = useState(false);
  const [backfillError, setBackfillError] = useState<string | null>(null);
  const [backfilledCount, setBackfilledCount] = useState<number | null>(null);

  const [mailchimpFinderOpen, setMailchimpFinderOpen] = useState(false);
  const [mailchimpSearchQuery, setMailchimpSearchQuery] = useState("");
  const [mailchimpSearchResults, setMailchimpSearchResults] = useState<
    MailchimpCampaignMatch[]
  >([]);
  const [mailchimpSearching, setMailchimpSearching] = useState(false);
  const [mailchimpSearchError, setMailchimpSearchError] = useState<
    string | null
  >(null);

  useEffect(() => {
    setMailchimpIdInput(step.mailchimp_campaign_id ?? "");
  }, [step.id, step.mailchimp_campaign_id]);

  const loadMailchimpSyncWarning = useCallback(async () => {
    try {
      const syncState = await dataAdapter.getMailchimpSyncState(
        campaignId,
        step.id,
      );
      setMailchimpSkipWarning(describeSkippedContacts(syncState));
    } catch (e) {
      console.error("marketing.campaign.mailchimp_sync_state.load.failed", e);
    }
  }, [dataAdapter, campaignId, step.id]);

  useEffect(() => {
    void loadMailchimpSyncWarning();
  }, [loadMailchimpSyncWarning]);

  // Whether the daily auto-activate job is on, so the send-date hint only
  // promises "CIS will activate this" when it will. Null until read (or if
  // it can't be) — the hint then promises neither.
  const [autoActivationEnabled, setAutoActivationEnabled] = useState<
    boolean | null
  >(null);
  // The weekdays we do not work (bank holidays, shutdowns), from the backend:
  // the activation date skips them as the backend's rule does (d88).
  const [closedDays, setClosedDays] = useState<string[]>([]);
  const needsActivationHint = step.status === "draft" && !step.follows_step_id;
  useEffect(() => {
    if (!needsActivationHint) return;
    let cancelled = false;
    dataAdapter
      .getCampaignAutoActivation()
      .then((state) => {
        if (cancelled) return;
        setAutoActivationEnabled(state.enabled);
        setClosedDays(state.closed_days ?? []);
      })
      .catch((e) => {
        console.error("marketing.campaign.auto_activation.load.failed", e);
      });
    return () => {
      cancelled = true;
    };
  }, [dataAdapter, needsActivationHint]);

  // An email send with nobody in it can't be activated (the backend refuses
  // it): say why before anyone presses Activate. An unknown count (null, on a
  // single-step response) doesn't block — the backend still refuses.
  const emptySendReason =
    step.status === "draft" && step.member_count === 0
      ? step.follows_step_id
        ? "Nobody is in this reminder yet. Generate the reminder audience first. If the reminder audience is still empty, nobody from the tranche is left to remind."
        : "Nobody is in this send, so there is nothing to activate."
      : null;

  async function handleActivate(confirmOverCap = false) {
    setActivating(true);
    setActivateError(null);
    try {
      const updated = await dataAdapter.activateMarketingCampaignStep(
        campaignId,
        step.id,
        { confirmOverCap },
      );
      onStepChange(updated);
      setCapDialogOpen(false);
      void loadMailchimpSyncWarning();
    } catch (e) {
      const capExceeded = extractActivationCapExceeded(e);
      if (capExceeded) {
        setCapWarnings(capExceeded.warnings);
        setCapDialogOpen(true);
        return;
      }
      console.error("marketing.campaign.activate.failed", e);
      const detail = e instanceof ApiError ? e.detail?.trim() : "";
      setActivateError(detail || "Could not activate. Please try again.");
    } finally {
      setActivating(false);
    }
  }

  async function handleSaveMailchimpId() {
    const value = mailchimpIdInput.trim();
    if (!value) return;
    setSavingMailchimpId(true);
    setMailchimpIdError(null);
    setMailchimpIdSaved(false);
    try {
      const updated = await dataAdapter.setStepMailchimpId(
        campaignId,
        step.id,
        value,
      );
      onStepChange(updated);
      setMailchimpIdInput(updated.mailchimp_campaign_id ?? "");
      setMailchimpIdSaved(true);
    } catch (e) {
      console.error("marketing.campaign.set_mailchimp_id.failed", e);
      const detail =
        e instanceof ApiError && e.status === 400 ? e.detail?.trim() : "";
      setMailchimpIdError(detail || "Could not save. Please try again.");
    } finally {
      setSavingMailchimpId(false);
    }
  }

  async function handleSaveSendDate(value: string) {
    if (!value || value === step.scheduled_send_date) return;
    setSavingSendDate(true);
    setSendDateError(null);
    setSendDateSaved(false);
    try {
      const updated = await dataAdapter.updateMarketingCampaignStep(
        campaignId,
        step.id,
        { scheduled_send_date: value },
      );
      onStepChange(updated);
      setSendDateInput(updated.scheduled_send_date ?? "");
      setSendDateSaved(true);
    } catch (e) {
      console.error("marketing.campaign.send_date.save.failed", e);
      setSendDateError("Could not save. Please try again.");
    } finally {
      setSavingSendDate(false);
    }
  }

  function openMailchimpFinder() {
    setMailchimpSearchQuery("");
    setMailchimpSearchResults([]);
    setMailchimpSearchError(null);
    setMailchimpFinderOpen(true);
  }

  async function handleSearchMailchimp() {
    const query = mailchimpSearchQuery.trim();
    if (!query) return;
    setMailchimpSearching(true);
    setMailchimpSearchError(null);
    try {
      const results = await dataAdapter.searchMailchimpCampaigns(query);
      setMailchimpSearchResults(results);
    } catch (e) {
      console.error("marketing.campaign.mailchimp_finder.search.failed", e);
      setMailchimpSearchError("Could not search Mailchimp. Please try again.");
    } finally {
      setMailchimpSearching(false);
    }
  }

  function handlePickMailchimpCampaign(match: MailchimpCampaignMatch) {
    setMailchimpIdInput(match.mailchimp_campaign_id);
    setMailchimpFinderOpen(false);
  }

  async function handleResync() {
    setResyncing(true);
    setResyncError(null);
    try {
      const state = await dataAdapter.resyncCampaignMailchimp(
        campaignId,
        step.id,
      );
      setMailchimpSkipWarning(describeSkippedContacts(state));
      setResyncedAt(state.last_synced_at);
    } catch (e) {
      console.error("marketing.campaign.mailchimp_resync.failed", e);
      setResyncError("Could not resync. Please try again.");
    } finally {
      setResyncing(false);
    }
  }

  async function handleBackfillTouches() {
    setBackfilling(true);
    setBackfillError(null);
    try {
      const result = await dataAdapter.backfillStepTouches(campaignId, step.id);
      setBackfilledCount(result.touch_count);
    } catch (e) {
      console.error("marketing.campaign.backfill_touches.failed", e);
      setBackfillError("Could not backfill from Mailchimp. Please try again.");
    } finally {
      setBackfilling(false);
    }
  }

  return (
    <div className="space-y-4">
      {step.status === "draft" && !step.follows_step_id && (
        <div className="rounded-lg border border-border bg-surface p-4 space-y-1.5">
          <Label htmlFor={`send-date-${step.id}`}>Send date</Label>
          <div className="flex items-center gap-2">
            <Input
              id={`send-date-${step.id}`}
              type="date"
              value={sendDateInput}
              onChange={(e) => {
                setSendDateInput(e.target.value);
                // "Saved" refers to the previous value — a new, unsaved
                // date must not sit next to it.
                setSendDateSaved(false);
                setSendDateError(null);
              }}
              onBlur={(e) => handleSaveSendDate(e.target.value)}
              disabled={savingSendDate}
              className="max-w-48"
            />
            {savingSendDate && (
              <span className="text-xs text-muted-foreground">Saving…</span>
            )}
            {!savingSendDate && sendDateSaved && (
              <span className="text-xs text-muted-foreground">Saved</span>
            )}
          </div>
          {sendDateError && (
            <p className="text-xs text-destructive">{sendDateError}</p>
          )}
          <p className="text-xs text-muted-foreground">
            The real Mailchimp send date.
            {sendDateInput &&
              ` ${activationHint(sendDateInput, autoActivationEnabled, "this", "email", closedDays)}`}
          </p>
        </div>
      )}
      {step.status === "draft" && step.follows_step_id && (
        <div className="rounded-lg border border-border bg-surface p-4 space-y-1.5">
          <Label>Send date</Label>
          {step.reminder_send_date ? (
            <>
              <p className="text-sm text-foreground">
                {formatDateOrDash(step.reminder_send_date)}
              </p>
              <p className="text-xs text-muted-foreground">
                Derived from its original tranche's send date plus the
                reminder's gap ({step.timing.offset_days ?? 0} days).
              </p>
            </>
          ) : (
            <p className="text-xs text-muted-foreground">
              This reminder's date isn't known yet — it's derived from its
              original tranche's send date once that tranche has a date. Set the
              gap in days on the tranche's own edit dialog.
            </p>
          )}
        </div>
      )}

      {mailchimpSkipWarning && (
        <div className="rounded-lg border border-amber-400/40 bg-amber-50 px-4 py-3 text-sm text-amber-700 dark:bg-amber-900/10 dark:text-amber-400">
          {mailchimpSkipWarning}
        </div>
      )}

      <div className="rounded-lg border border-border bg-surface p-4 space-y-4">
        <div className="grid gap-4 sm:grid-cols-[1fr_auto]">
          <div className="space-y-1.5">
            <Label htmlFor={`mailchimp-id-${step.id}`}>
              Mailchimp campaign ID
            </Label>
            <div className="flex gap-2">
              <Input
                id={`mailchimp-id-${step.id}`}
                value={mailchimpIdInput}
                onChange={(e) => setMailchimpIdInput(e.target.value)}
                placeholder="e.g. b1f8a9c2d3"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={openMailchimpFinder}
              >
                <Search className="size-4" />
                Find
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              From Mailchimp, once studio has sent this send's email. Pasting it
              in unlocks its engagement data.
            </p>
          </div>
          <div className="flex items-end gap-2">
            {step.status === "draft" && (
              <Button
                onClick={() => handleActivate()}
                disabled={activating || emptySendReason !== null}
                aria-describedby={
                  emptySendReason ? `empty-send-${step.id}` : undefined
                }
              >
                {activating ? "Activating…" : "Activate"}
              </Button>
            )}
            {(step.status === "active" || step.status === "closed") && (
              <Button
                variant="outline"
                onClick={handleResync}
                disabled={resyncing}
              >
                {resyncing ? "Fixing…" : "Fix pending touches"}
              </Button>
            )}
            {isAdmin &&
              (step.status === "active" || step.status === "closed") &&
              step.mailchimp_campaign_id && (
                <Button
                  variant="outline"
                  onClick={handleBackfillTouches}
                  disabled={backfilling}
                >
                  {backfilling ? "Backfilling…" : "Backfill touch history"}
                </Button>
              )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={handleSaveMailchimpId}
            disabled={savingMailchimpId || !mailchimpIdInput.trim()}
          >
            {savingMailchimpId ? "Saving…" : "Save Mailchimp ID"}
          </Button>
          {mailchimpIdSaved && !savingMailchimpId && (
            <span className="text-sm text-muted-foreground">Saved.</span>
          )}
          {mailchimpIdError && (
            <span className="text-sm text-red-600">{mailchimpIdError}</span>
          )}
        </div>
        {emptySendReason && (
          <p
            id={`empty-send-${step.id}`}
            className="text-xs text-muted-foreground"
          >
            {emptySendReason}
          </p>
        )}
        {activateError && (
          <p className="text-xs text-red-600">{activateError}</p>
        )}
        {resyncedAt && !resyncing && !resyncError && (
          <p className="text-xs text-muted-foreground">
            Synced {formatDateTimeOrDash(resyncedAt)}
          </p>
        )}
        {resyncError && <p className="text-xs text-red-600">{resyncError}</p>}
        {backfilledCount !== null && !backfilling && !backfillError && (
          <p className="text-xs text-muted-foreground">
            {backfilledCount > 0
              ? `Backfilled ${backfilledCount} touch${backfilledCount === 1 ? "" : "es"} from Mailchimp.`
              : "No new touches found — Mailchimp has nothing left to backfill for this send."}
          </p>
        )}
        {backfillError && (
          <p className="text-xs text-red-600">{backfillError}</p>
        )}
      </div>

      <AlertDialog open={capDialogOpen} onOpenChange={setCapDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {capWarnings.length} contact{capWarnings.length === 1 ? "" : "s"}{" "}
              at the email frequency cap
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-left">
                <p>
                  These contacts have already been sent 3 or more campaigns in
                  the last 30 days. Sending this one too may feel like hounding
                  — confirm you still want to include them.
                </p>
                <ul className="list-disc pl-5 max-h-64 overflow-y-auto">
                  {capWarnings.map((w) => (
                    <li key={w.personId}>
                      {w.personName} — {w.recentCampaignCount} campaigns in the
                      last 30 days
                    </li>
                  ))}
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleActivate(true)}>
              Send anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={mailchimpFinderOpen} onOpenChange={setMailchimpFinderOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Find Mailchimp campaign</DialogTitle>
            <DialogDescription>
              Search by campaign name or subject line — Mailchimp's own UI never
              shows the ID this field needs.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="flex gap-2">
              <Input
                autoFocus
                value={mailchimpSearchQuery}
                onChange={(e) => setMailchimpSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void handleSearchMailchimp();
                }}
                placeholder="e.g. supplier update"
              />
              <Button
                type="button"
                onClick={handleSearchMailchimp}
                disabled={mailchimpSearching || !mailchimpSearchQuery.trim()}
              >
                {mailchimpSearching ? "Searching…" : "Search"}
              </Button>
            </div>
            {mailchimpSearchError && (
              <p className="text-sm text-red-600">{mailchimpSearchError}</p>
            )}
            {!mailchimpSearching &&
              !mailchimpSearchError &&
              mailchimpSearchResults.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  {mailchimpSearchQuery.trim()
                    ? "No matching campaigns. Try a different search term."
                    : "Type part of the campaign's name or subject line, or the number from a Mailchimp report link, then search."}
                </p>
              )}
            {mailchimpSearchResults.length > 0 && (
              <ul className="divide-y divide-border rounded-lg border border-border max-h-72 overflow-y-auto">
                {mailchimpSearchResults.map((match) => (
                  <li key={match.mailchimp_campaign_id}>
                    <button
                      type="button"
                      onClick={() => handlePickMailchimpCampaign(match)}
                      className="w-full text-left p-3 hover:bg-surface-hover transition-colors"
                    >
                      <div className="font-medium text-foreground">
                        {match.title}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {match.subject_line}
                      </div>
                      {match.status === "sending" ? (
                        <div className="text-xs text-muted-foreground mt-0.5">
                          Still sending (automation flow email)
                        </div>
                      ) : (
                        match.send_time && (
                          <div className="text-xs text-muted-foreground mt-0.5">
                            Sent {formatDateOrDash(match.send_time)}
                          </div>
                        )
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setMailchimpFinderOpen(false)}
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Tabs defaultValue="engagement" className="gap-4">
        <TabsList variant="underline">
          <TabsTrigger value="engagement">Engagement</TabsTrigger>
          <TabsTrigger value="preview">Email preview</TabsTrigger>
        </TabsList>
        <TabsContent value="engagement">
          <CampaignEngagementTab campaignId={campaignId} stepId={step.id} />
        </TabsContent>
        <TabsContent value="preview">
          <CampaignEmailPreviewTab campaignId={campaignId} stepId={step.id} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
